import { useQuery } from '@tanstack/react-query'
import type { LinkProps } from '@tanstack/react-router'
import { useState } from 'react'
import { useSession } from '@/entities/session'
import { projectTasksQueryOptions, type Task } from '@/entities/task'
import { CreateTaskDialog } from '@/features/create-task'
import { EditTaskDialog, useCompleteTask } from '@/features/edit-task'
import { Button } from '@/shared/ui/button'
import { EmptyState } from '@/shared/ui/empty-state'
import { Icon } from '@/shared/ui/icon'
import { Spinner } from '@/shared/ui/spinner'
import { StatusScreen } from '@/shared/ui/status-screen'
import { toast } from '@/shared/ui/toast'
import { ZoneNote } from '@/shared/ui/zone-badge'
import { TaskRow } from './task-row'

interface TaskListProps {
  projectId: string
  isEmployee: boolean
  /** Ссылка на страницу задачи; без неё тайтл статичен (виджет route-agnostic). */
  taskLink?: (taskId: string) => LinkProps
}

// Задачи проекта: список + создание/выполнение/правка. Задачи обезличены —
// исполнителя нет, заказчик видит все задачи сделки, правит/удаляет только свои.
export function TaskList({ projectId, isEmployee, taskLink }: TaskListProps) {
  const { session } = useSession()
  const tasksQ = useQuery(projectTasksQueryOptions(projectId))
  const complete = useCompleteTask(projectId)
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<Task | null>(null)

  if (tasksQ.isPending) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }
  if (tasksQ.error) {
    return <StatusScreen title="Не удалось загрузить задачи" message="Обновите страницу." />
  }

  const tasks = tasksQ.data

  function toggle(task: Task, done: boolean) {
    complete.mutate({ id: task.id, done }, { onError: () => toast.error('Не удалось обновить') })
  }

  // Заказчик правит/удаляет только созданные им задачи — тот же гейт, что у бэка.
  const canEdit = (task: Task): boolean =>
    isEmployee || (task.created_by?.kind === 'customer' && task.created_by.id === session?.id)

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        {/* Моменты строк (создана/выполнено) подписаны зоной один раз здесь;
            срок задачи — календарный день, зоны у него нет, о чём и сказано. */}
        <ZoneNote note="срок задачи — календарный день, без времени" />
        <Button size="sm" variant="secondary" onClick={() => setCreateOpen(true)}>
          <Icon name="plus" size={16} />
          Задача
        </Button>
      </div>

      {tasks.length === 0 ? (
        <EmptyState title="Задач нет" description="Заведите первую задачу по проекту." />
      ) : (
        <ul className="flex flex-col gap-2">
          {tasks.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              isEmployee={isEmployee}
              canToggle={isEmployee}
              onToggle={(done) => toggle(task, done)}
              {...(taskLink ? { taskLink } : {})}
              {...(canEdit(task) ? { onEdit: () => setEditing(task) } : {})}
            />
          ))}
        </ul>
      )}

      {createOpen && (
        <CreateTaskDialog projectId={projectId} open onClose={() => setCreateOpen(false)} />
      )}
      {editing && (
        <EditTaskDialog
          task={editing}
          projectId={projectId}
          open
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}
